/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useProcedure from 'procedure/useProcedure';
import { CURRENCY_POSITION_TYPE } from 'shared/enums/FeeCellType';
import { useConfiguration } from '../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../redux/enums/ConfigResourceProps';
import { capitalizeWords, twoDecimalsFormatter } from '../../shared/utils/stringUtils';
import { shortFormatDate } from '../../shared/utils/dateUtils';
import { attachmentTypes } from '../draft/attachments/services/constants';
import { getSubmissionSummaryContent } from '../../shared/submission/submissionPropertyAccessor';
import { getFileIconComponent } from '../../shared/utils/attachmentUtils';
import { createAttachmentSection, getAttachmentsOfCategory } from './summaryCards/attachments/attachments.helper';
import {
  createSelectedFeesSection,
  createMethodOfPaymentEntry,
  createPaymentAttachmentEntries,
  createTotalFeesFooterEntries,
} from './summaryCards/fees/feesCard.helper';
import { SubmissionSectionType } from '../common/SummaryCard/CardBody/SubmissionSectionType';
import { useAttachmentsConfig } from '../draft/attachments/hooks/useAttachmentsConfig';
import { useAttachmentMetaFields } from '../draft/attachments/uploader/meta/useAttachmentMetaFields';

export default function useSummary(submission, uiSchema = {}, hideLinkTo = false, jsonSchema = {}) {
  const { t, i18n } = useTranslation();
  const currentProcedure = useProcedure();

  const frontendCustomisationConfig = useConfiguration(ConfigResourceProps.FRONTEND_CUSTOMISATION);
  const customSAType = frontendCustomisationConfig?.resource?.customSA;

  const submissionSchemaConfig = useConfiguration(
    ConfigResourceProps.SUBMISSION_VALIDATION_SCHEMA,
    currentProcedure.id,
  );
  const {
    technicalAttachmentTypes,
    additionalAttachmentTypes,
    spcAttachmentTypes,
    saAttachmentTypes,
  } = useAttachmentsConfig();
  const { extractAllDropdownNames } = useAttachmentMetaFields();

  // map of summary sections with an array of entries for each section
  const [summarySections, setSummarySections] = useState({});

  const frontendCustomisationDateFormat = frontendCustomisationConfig.resource?.dateFormat;
  const schema = submissionSchemaConfig.resource;
  const currencyIsoCode = frontendCustomisationConfig?.resource?.currency?.ISO || i18n.language;

  const amountFormatter = twoDecimalsFormatter(currencyIsoCode);

  const getSummarySectionEntries = summaryEntryType => summarySections[summaryEntryType] ?? [];
  const mapEntries = _entries =>
    _entries.map(entry => ({
      ...entry,
      ...(hideLinkTo && { hideLinkTo }),
    }));

  useEffect(() => {
    // TODO temporary solution only for parties and declarations
    //  until we migrate all other sections to uiSchema

    const summarySectionsTemp = {};

    const summaryContent = getSubmissionSummaryContent(submission, uiSchema, t, i18n, schema);
    const currencyPosition = submission?.fees?.feesSelection?.currencyPosition === CURRENCY_POSITION_TYPE?.AFTER;
    Object.entries(summaryContent).forEach(([sectionName, entries]) => {
      Object.assign(summarySectionsTemp, { [sectionName]: mapEntries(entries) });
    });

    if (submission?.fees) {
      let feeSummaryEntries = [];
      if (!summarySectionsTemp[SubmissionSectionType.FEES]) {
        const enableMethodOfPaymentEntry = uiSchema?.fees?.feesSelection?.['ui:data']?.enableMethodOfPayment;

        if (enableMethodOfPaymentEntry || typeof enableMethodOfPaymentEntry === 'undefined') {
          const methodOfPaymentEntry = createMethodOfPaymentEntry(
            submission?.fees,
            submissionSchemaConfig?.ready,
            schema,
            t,
          );
          feeSummaryEntries.push(methodOfPaymentEntry);
        }
      } else {
        feeSummaryEntries = summarySectionsTemp[SubmissionSectionType.FEES];
      }

      const paymentDocuments = getAttachmentsOfCategory(submission, attachmentTypes.PAYMENT);
      feeSummaryEntries.push(...createPaymentAttachmentEntries(paymentDocuments, t));
      feeSummaryEntries.push(
        createSelectedFeesSection(submission.fees?.feesSelection, amountFormatter, t, currencyPosition, uiSchema),
      );
      feeSummaryEntries.push(
        ...createTotalFeesFooterEntries(submission.fees?.feesSelection, amountFormatter, t, currencyPosition, uiSchema),
      );

      if (feeSummaryEntries) {
        Object.assign(summarySectionsTemp, { [SubmissionSectionType.FEES]: mapEntries(feeSummaryEntries) });
      }
    }

    // TODO remove that if once all the countries create receipt configuration inside uiSchema files
    if (submission?.receipt && !summarySectionsTemp[SubmissionSectionType.RECEIPT]) {
      Object.assign(summarySectionsTemp, {
        [SubmissionSectionType.RECEIPT]: [
          {
            title: t('receipts.sender'),
            value: capitalizeWords(submission.receipt.sender) || '',
          },
          {
            title: t('receipts.date'),
            value: shortFormatDate(new Date(submission.receipt.senderDate), frontendCustomisationDateFormat) || '',
          },
          {
            title: t('receipts.application.number'),
            value: submission.receipt.applicationNumber || '',
          },
        ],
      });
    }

    if (submission?.attachments) {
      const sequenceListDocument = submission?.attachments.filter(
        file => file.attachmentSubType === attachmentTypes.SEQ,
      );
      if (sequenceListDocument.length > 0) {
        const bioEntries = [
          {
            sectionHeader: t('seql.attachments.title'),
            sectionType: 'seqlAttachments',
            type: SubmissionSectionType.SEQLBIO,
            items: [
              {
                title: t('seql.attachments.title'),
                value: sequenceListDocument[0].originalFileName,
                icon: getFileIconComponent(sequenceListDocument[0]),
              },
            ],
          },
        ];
        const seqlBioFromSummaryContent = summaryContent?.seqlBio ? summaryContent?.seqlBio : [];
        Object.assign(summarySectionsTemp, {
          [SubmissionSectionType.SEQLBIO]: mapEntries(bioEntries.concat(seqlBioFromSummaryContent)),
        });
      }
    }

    let attachmentCardEntries = [];

    if (technicalAttachmentTypes?.length > 0) {
      const technicalDocuments = getAttachmentsOfCategory(submission, attachmentTypes.TECHNICAL);
      const technicalAttachmentsEntry = createAttachmentSection(
        technicalDocuments,
        attachmentTypes.TECHNICAL,
        t('technical.documents'),
        t('summary.see.all.documents.technical'),
        t,
        extractAllDropdownNames(attachmentTypes.TECHNICAL, jsonSchema),
      );
      attachmentCardEntries = attachmentCardEntries.concat(technicalAttachmentsEntry);
    }
    if (additionalAttachmentTypes?.length > 0) {
      const additionalDocuments = getAttachmentsOfCategory(submission, attachmentTypes.ADDITIONAL);
      const additionalAttachmentsEntry = createAttachmentSection(
        additionalDocuments,
        attachmentTypes.ADDITIONAL,
        t('additional.documents'),
        t('summary.see.all.documents.additional'),
        t,
        extractAllDropdownNames(attachmentTypes.ADDITIONAL, jsonSchema),
      );
      attachmentCardEntries = attachmentCardEntries.concat(additionalAttachmentsEntry);
    }
    if (spcAttachmentTypes?.length > 0) {
      const spcDocuments = getAttachmentsOfCategory(submission, attachmentTypes.SPC);
      const spcAttachmentsEntry = createAttachmentSection(
        spcDocuments,
        attachmentTypes.SPC,
        t('documents.general'),
        t('summary.see.all.documents'),
        t,
        extractAllDropdownNames(attachmentTypes.SPC, jsonSchema),
      );
      attachmentCardEntries = attachmentCardEntries.concat(spcAttachmentsEntry);
    }

    if (saAttachmentTypes?.length > 0 || !!customSAType) {
      const saDocuments = !customSAType
        ? getAttachmentsOfCategory(submission, attachmentTypes.SA)
        : getAttachmentsOfCategory(submission, customSAType);

      const saAttachmentsEntry = createAttachmentSection(
        saDocuments,
        attachmentTypes.SA,
        t('documents.general'),
        t('summary.see.all.documents'),
        t,
        extractAllDropdownNames(attachmentTypes.SA, jsonSchema),
      );
      attachmentCardEntries = attachmentCardEntries.concat(saAttachmentsEntry);
    }

    Object.assign(summarySectionsTemp, { [SubmissionSectionType.ATTACHMENTS]: mapEntries(attachmentCardEntries) });

    setSummarySections(summarySectionsTemp);
  }, [submission, submissionSchemaConfig.ready]);

  return {
    getSummarySectionEntries,
  };
}
